import {action, computed, makeObservable, observable, override} from 'mobx'
import noPhotoPic from '@/src/shared/assets/media/no-photo.png'
import isNumber from 'lodash/isNumber'
import {Shop} from '@/src/entities/shop'
import type {
  Product,
  ProductPrice,
  ProductProperties,
  ProductSimple,
  ProductStock,
  ProductVariant,
} from '@/src/core/domain/entities/product'
import type {IProductListItemViewModel, WarehouseAvailable} from './list-item-types'
import {ProductImagesStore} from './images-store'
import type {BasketStoreInterface} from '@/src/presentation/stores/basket/basket.store.interface'

export {ProductImagesStore}
export type {IProductListItemViewModel, WarehouseAvailable}

export class ProductListItemViewModel implements IProductListItemViewModel {
  @observable
  product!: Product

  @observable
  basket: Nullable<BasketStoreInterface> = null

  @observable
  images!: ProductImagesStore

  @observable
  loading = false

  @observable
  onClickBuyButtonDesktop?: (variant?: ProductVariant) => void
  @observable
  onClickBuyButtonMobile?: (variant?: ProductVariant) => void

  constructor(
    product: Product,
    onClickBuyButtonDesktop?: (variant?: ProductVariant) => void,
    onClickBuyButtonMobile?: (variant?: ProductVariant) => void,
    basket?: BasketStoreInterface,
  ) {
    if (basket) {
      this.basket = basket
    }
    this.product = product
    this.images = new ProductImagesStore()
    this.onClickBuyButtonDesktop = onClickBuyButtonDesktop
    this.onClickBuyButtonMobile = onClickBuyButtonMobile

    makeObservable(this)
  }

  @computed
  get key() {
    return this.product.id
  }

  @computed
  get listProperties() {
    return Object.keys(this.product.properties ?? {}).reduce((acc, propertyKey) => {
      if (this.product.properties![propertyKey]!.settings.show_in_list) {
        acc[propertyKey] = this.product.properties![propertyKey]!
      }
      return acc
    }, {} as ProductProperties)
  }

  @computed
  get cardProperties() {
    return Object.keys(this.product.properties ?? {}).reduce((acc, propertyKey) => {
      if (this.product.properties![propertyKey]!.settings.show_in_card) {
        acc[propertyKey] = this.product.properties![propertyKey]!
      }
      return acc
    }, {} as ProductProperties)
  }

  @computed
  get available() {
    return false
  }

  getWarehousesByStocks(warehouses: Record<Id, Shop>, stocks: ProductStock[]): WarehouseAvailable[] {
    return stocks
      .filter(stock => stock?.amount && isNumber(stock?.amount) && stock?.amount > 0)
      .filter(stock => warehouses[stock.warehouse_id])
      .map(
        stock =>
          ({
            ...warehouses[stock.warehouse_id]!,
            amount: stock.amount,
          }) satisfies WarehouseAvailable,
      )
      .filter(warehouse => !!warehouse)
  }

  @computed
  get warehouseAvailableList() {
    return (_warehouses?: Record<Id, Shop>) => {
      return [] as WarehouseAvailable[]
    }
  }

  @computed
  get inBasket() {
    return false
  }

  @action.bound
  async onClickBuyButton() {
    this.setLoading(true)
    await this.basket?.addProduct(this.product)
    this.setLoading(false)
  }

  @action.bound
  setBasket(basket: BasketStoreInterface) {
    this.basket = basket
  }

  @computed
  get price(): Nullable<ProductPrice> {
    throw Error('No Implementation')
  }

  @computed
  get mainImage() {
    return this.product.images.length > 0 ? `${this.product.images[0]!}` : noPhotoPic
  }

  @computed
  get backImage() {
    return this.product.images.length > 1
      ? `${this.product.images[1]!}`
      : this.product.images.length > 0
        ? `${this.product.images[0]!}`
        : noPhotoPic
  }

  @action.bound
  setLoading(loading: boolean) {
    this.loading = loading
  }
}

export class ProductSimpleListItemViewModel extends ProductListItemViewModel {
  declare product: ProductSimple

  constructor(
    product: ProductSimple,
    onClickBuyButtonDesktop?: (variant?: ProductVariant) => void,
    onClickBuyButtonMobile?: (variant?: ProductVariant) => void,
    basket?: BasketStoreInterface,
  ) {
    super(product, onClickBuyButtonDesktop, onClickBuyButtonMobile, basket)

    this.product = product
    this.images = new ProductImagesStore()

    makeObservable(this)
  }

  @override
  get price(): Nullable<ProductPrice> {
    return this.product.price
  }

  @override
  get available() {
    return this.product.available
  }

  @override
  get inBasket() {
    if (this.basket) {
      return this.basket.haveProduct(this.product)
    }
    return false
  }
}
